import estudoApi from "@Config/api/estudoApi";

/**
 * Cliente das rotas do estudo-rede-api usadas pelo fluxo interno.
 * Tipos espelham src/api/src/lib/analyze.ts e src/api/src/lib/simulacoes.ts.
 */

// Ponto do Mapa de Rota (heatmap). O backend usa `lon`; o Google Maps usa `lng`.
export interface HeatPoint {
  lat: number;
  lon: number;
  liters: number;
  count: number;
  valorTotal: number;
  economiaTotal: number;
}

// Alternativa RF sugerida para um posto não credenciado Rede Frota.
export interface PostoSugerido {
  seq: number;
  razaoSocial: string | null;
  cidadeNorm: string | null;
  estado: string | null;
  lat: number;
  lon: number;
  dieselPrice: number | null; // melhor preço do indicado (menor acordo, senão padrão) — o "novo preço"
  padraoVigente: number | null; // preço padrão (balcão) vigente do posto indicado
  distanciaKm: number;
  mesmaBr: boolean; // a indicação está na MESMA rodovia do posto de origem?
  brComum: number | null; // nº da BR compartilhada quando mesmaBr=true (ex.: 369)
}

// Um preço distinto que o cliente pagou (composição da média do "Pago").
export interface ComposicaoPagoItem {
  preco: number;
  litros: number;
  percentual: number; // 0..1 sobre todos os litros do posto
  dataMin: string | null;
  dataMax: string | null;
}

// Linha da tabela "por período" (régua diária): pago × menor do posto, cronológica.
export interface ComposicaoPeriodoItem {
  dataMin: string | null;
  dataMax: string | null;
  litros: number;
  percentual: number;
  pago: number;
  menorDoPosto: number | null;
}

// As duas economias do modelo novo (espelha src/api/src/lib/analyze.ts):
//  - realista: vs. MENOR PREÇO USADO (teve venda no posto — prova de que rola).
//  - agressiva: vs. MENOR ACORDO disponível hoje (vigente, incl. nunca praticado).
export interface Economia {
  porLitro: number;
  total: number; // por posto: × litros do posto; global: soma dos postos
}

// Consumo FORA da plataforma (abastecimento manual, ind_origem='M') — separado da
// análise on-platform. É a oportunidade de trazer esse volume para o cartão RF.
export interface ForaPlataforma {
  litros: number;
  valorPago: number;
  precoMedio: number;
  nPostos: number;
  dataMin: string | null;
  dataMax: string | null;
}

// Detalhamento por produto (balde S-10/S-500) dentro de um posto. Cada balde comparado
// contra as refs do SEU produto. Espelha ProdutoBreakdown de analyze.ts.
export interface ProdutoBreakdown {
  produto: string; // 'DIESEL S-10' | 'DIESEL S-500'
  litros: number;
  precoMedioPago: number;
  menorPrecoMedioDiario: number | null;
  medioPrecoUsado: number | null;
  maiorPrecoUsado: number | null;
  economiaTotal: number;
  composicaoPeriodo?: ComposicaoPeriodoItem[];
}

export interface PostoDetalhe {
  cnpj: string | null;
  nome: string | null;
  cidade: string | null;
  estado: string | null;
  criterio: string;
  litros: number;
  precoMedioPago: number;
  // Estatística de PREÇO USADO (vendas reais na janela do arquivo) do posto.
  menorPrecoUsado: number | null;
  medioPrecoUsado: number | null;
  maiorPrecoUsado: number | null;
  litrosMenorPrecoUsado: number | null;
  // Régua diária: média simples dos menores diários (métrica principal do "menor preço").
  menorPrecoMedioDiario: number | null;
  // Estatística de ACORDO disponível hoje (vigente, incl. não praticado) do posto.
  menorPrecoAcordo: number | null;
  medioPrecoAcordo: number | null;
  maiorPrecoAcordo: number | null;
  padraoVigente: number | null;
  // Duas economias.
  economiaRealista: Economia | null; // vs. menor usado
  economiaAgressiva: Economia | null; // vs. menor acordo
  // O menor usado já praticado no posto É o menor acordo disponível.
  menorUsadoEhAcordo: boolean;
  casou: boolean;
  // Compat (front): referência/economia "principal" = a REALISTA.
  precoReferencia: number | null; // = menor usado
  economiaPorLitro: number | null;
  economiaTotal: number;
  sugestao?: PostoSugerido | null;
  composicaoPago?: ComposicaoPagoItem[];
  composicaoPeriodo?: ComposicaoPeriodoItem[];
  // Detalhamento por balde (S-10/S-500) presente no posto.
  porProduto?: ProdutoBreakdown[];
}

// Result completo (rota interna) ou parcial (rota pública /estudo, sanitizada).
// Campos de economia são marcados opcionais pois a rota pública os remove (ver
// CAMPOS_ECONOMIA em analyze.ts). `combustivel` e `menorUsadoEhAcordo` sempre vêm.
export interface AnalyzeResult {
  litrosTotais: number;
  litrosCasados: number;
  cobertura: number;
  combustivel: string;
  combustiveis?: string[]; // baldes presentes (S-10 antes de S-500) — para o selo
  aditivadoAproximado?: boolean; // houve aditivado/comum tratado como o base → aviso
  precoMedioPago?: number;
  precoMedioReferencia?: number;
  // Estatística global (postos casados): USADO e ACORDO.
  menorPrecoUsado?: number | null;
  medioPrecoUsado?: number | null;
  maiorPrecoUsado?: number | null;
  litrosMenorPrecoUsado?: number | null;
  menorPrecoAcordo?: number | null;
  medioPrecoAcordo?: number | null;
  maiorPrecoAcordo?: number | null;
  padraoVigente?: number | null;
  menorUsadoEhAcordo: boolean;
  // Economia REALISTA (vs. menor usado) — espelhada nos campos compat abaixo.
  economiaRealistaPorLitro?: number;
  economiaRealistaPeriodo?: number;
  economiaRealistaMensal?: number;
  economiaRealistaAnual?: number;
  // Economia AGRESSIVA (vs. menor acordo disponível hoje).
  economiaAgressivaPorLitro?: number;
  economiaAgressivaPeriodo?: number;
  economiaAgressivaMensal?: number;
  economiaAgressivaAnual?: number;
  // Compat: economia "principal" = a REALISTA.
  economiaPorLitro?: number;
  economiaPeriodo?: number;
  meses: number;
  economiaMensalEstimada?: number;
  economiaAnualEstimada?: number;
  mapaRota?: HeatPoint[];
  porPosto?: PostoDetalhe[];
  moeda: "BRL";
  janelaMeses: number;
  dataMin?: string | null;
  dataMax?: string | null;
  needsRecentData: boolean;
  registrosForaJanela: number;
  litrosForaJanela: number;
  registrosSemData: number;
  combustivelAssumido?: boolean;
  economiaRelevante?: boolean;
  // Consumo fora da plataforma (manual) — separado; null/ausente quando não há.
  foraPlataforma?: ForaPlataforma | null;
}

// Resposta de POST /interno/analyze-file (result COMPLETO, sem sanitização).
export interface AnalyzeFileResponse {
  result: AnalyzeResult;
  codigo: string;
  token: string;
  estudoUrl?: string;
  arquivoUrl?: string;
  pdfUrl?: string;
  linhasLidas?: number;
  consultor?: { email: string | null; name: string | null };
}

// Resposta de GET /estudo/:token (SimulacaoView — result sanitizado pelo corte).
export interface EstudoResponse {
  codigo: string;
  createdAt: string;
  empresa: string | null;
  result: AnalyzeResult;
  economiaRelevante: boolean;
  estudoUrl?: string;
  arquivoUrl: string;
}

// Resposta de GET /interno/estudo/:token (autenticada). Result COMPLETO, sem o
// corte de economia do funil — inclui empresa/cnpj/createdAt para o cabeçalho.
export interface EstudoInternoResponse {
  result: AnalyzeResult;
  codigo: string;
  token: string;
  empresa: string | null;
  cnpj: string | null;
  createdAt: string;
  estudoUrl: string;
  arquivoUrl: string;
  pdfUrl?: string;
}

// Item de GET /interno/simulacoes.
export interface SimulacaoInternaItem {
  token: string;
  codigo: string;
  generatedAt: string;
  razaoSocial: string | null;
  cnpj: string | null;
  origem: string;
  consultorNome: string | null; // autor do estudo (só o nome)
  litrosTotais: number | null;
  economiaMensal: number | null;
  convertido: boolean; // prospect cujo CNPJ também foi analisado como cliente
}

export interface ListSimulacoesResponse {
  items: SimulacaoInternaItem[];
  total: number;
}

/**
 * POST /interno/analyze-file (multipart). O `estudoApi` tem
 * Content-Type: application/json por padrão. Se esse header vazar para uma
 * requisição com FormData, o transformRequest do axios 1.x serializa o FormData
 * para JSON e o ARQUIVO SE PERDE (multer devolve 400). Removemos o header via
 * transformRequest (determinístico) e deixamos o browser definir o boundary do
 * multipart. O campo do arquivo é exatamente `file`; razaoSocial (obrigatório)
 * + cnpj vão no mesmo form. Os interceptors (Bearer/x-customer-id) rodam à parte.
 */
export const postAnalyzeFile = async (input: {
  file: File;
  razaoSocial: string;
  cnpj?: string;
  produto?: string; // informado pelo consultor quando o arquivo não tem coluna de item
}): Promise<AnalyzeFileResponse> => {
  const form = new FormData();
  form.append("file", input.file);
  form.append("razaoSocial", input.razaoSocial);
  if (input.cnpj) form.append("cnpj", input.cnpj);
  if (input.produto) form.append("produto", input.produto);

  const { data } = await estudoApi.post<AnalyzeFileResponse>(
    "/interno/analyze-file",
    form,
    {
      transformRequest: [
        (payload, headers) => {
          headers.delete("Content-Type");
          return payload;
        },
      ],
    }
  );
  return data;
};

/**
 * POST /interno/clientes/:id/analyze — Item 2 (avaliar cliente atual). O backend
 * puxa os abastecimentos do cliente da origem (não é upload) e roda o mesmo estudo.
 * Enviamos razaoSocial+cnpj (vindos de getMyClients) para popular empresa/cnpj do
 * estudo. Mesmo shape de resposta do /interno/analyze-file.
 */
export const postClienteAnalyze = async (input: {
  customerId: number;
  razaoSocial: string;
  cnpj?: string;
}): Promise<AnalyzeFileResponse> => {
  const { data } = await estudoApi.post<AnalyzeFileResponse>(
    `/interno/clientes/${input.customerId}/analyze`,
    { razaoSocial: input.razaoSocial, cnpj: input.cnpj }
  );
  return data;
};

/** GET /estudo/:token — carrega um estudo já persistido (usado no histórico). */
export const getEstudo = async (token: string): Promise<EstudoResponse> => {
  const { data } = await estudoApi.get<EstudoResponse>(`/estudo/${token}`);
  return data;
};

/**
 * GET /interno/estudo/:token — carrega um estudo persistido pela rota AUTENTICADA
 * (Bearer + x-customer-id via estudoApi). Devolve o result COMPLETO, sem o corte de
 * economia que o /estudo/:token público aplica — usado no detalhe do histórico.
 */
export const getEstudoInterno = async (
  token: string
): Promise<EstudoInternoResponse> => {
  const { data } = await estudoApi.get<EstudoInternoResponse>(
    `/interno/estudo/${token}`
  );
  return data;
};

/** GET /interno/simulacoes — histórico dos estudos do time comercial. */
export const listSimulacoes = async (params?: {
  limit?: number;
  offset?: number;
  q?: string;
}): Promise<ListSimulacoesResponse> => {
  const { data } = await estudoApi.get<ListSimulacoesResponse>(
    "/interno/simulacoes",
    { params }
  );
  return data;
};

/**
 * Baixa um arquivo INTERNO (relatório PDF / estudo XLSX / arquivo cru) da rota
 * autenticada /interno/files. NÃO usar <a href> direto: a rota exige Bearer, e um
 * link simples não manda o token (401 → logout). Aqui o estudoApi injeta o Bearer;
 * o arquivo vem como blob e disparamos o download no navegador.
 */
export const downloadInternoFile = async (
  url: string,
  filename?: string
): Promise<void> => {
  const { data } = await estudoApi.get<Blob>(url, { responseType: "blob" });
  const blobUrl = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename || url.split("/").pop() || "arquivo";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
};

/** GET /config — versão do backend (backVersion) do estudo-rede-api. Rota pública. */
export interface EstudoConfig {
  canalEntrega?: string;
  backVersion?: string;
  // Fonte única do controle de acesso: nomes das roles do Keycloak + se o gate
  // está ligado. O enforcement REAL é por rota no backend; aqui é só para a UX.
  acesso?: {
    gateEnabled: boolean;
    roles: { base: string; externos: string; verTodos?: string; relatorios?: string; admin?: string; precos?: string };
  };
}
export const getConfig = async (): Promise<EstudoConfig> => {
  const { data } = await estudoApi.get<EstudoConfig>("/config");
  return data;
};

// ── Relatórios (área interna gateada por analise_relatorios) ──
export interface RelatorioEscopo {
  origens: string[] | null;
  verTodos: boolean;
  aviso: string | null;
}

export interface PostoForaRow {
  postoCnpj: string;
  cidade: string | null;
  uf: string | null;
  origem: string;
  nAbastecimentos: number;
  litros: number;
  valor: number;
  dataMin: string | null;
  dataMax: string | null;
  nEmpresas: number;
}
export interface RelatorioPostosForaResp {
  items: PostoForaRow[];
  escopo: RelatorioEscopo;
}

export interface PrometidoRow {
  cnpj: string;
  empresa: string | null;
  litrosMesPrometido: number;
  litrosMesAtual: number;
  delta: number;
  deltaPct: number | null;
  estudoProspectAt: string;
  estudoClienteAt: string;
}
export interface RelatorioPrometidoResp {
  items: PrometidoRow[];
  escopo: RelatorioEscopo;
}

export const getRelatorioPostosForaRede = async (params?: {
  origem?: "prospect" | "cliente" | "ambos";
  periodoDias?: number;
  uf?: string;
}): Promise<RelatorioPostosForaResp> => {
  const { data } = await estudoApi.get<RelatorioPostosForaResp>(
    "/interno/relatorios/postos-fora-rede",
    { params }
  );
  return data;
};

export const getRelatorioPrometidoVsAtual =
  async (): Promise<RelatorioPrometidoResp> => {
    const { data } = await estudoApi.get<RelatorioPrometidoResp>(
      "/interno/relatorios/prometido-vs-atual"
    );
    return data;
  };

// ── P2 ──
export interface RankingRow {
  postoCnpj: string;
  cidade: string | null;
  uf: string | null;
  nAbastecimentos: number;
  litros: number;
  valor: number;
  nEmpresas: number;
}
export interface RelatorioRankingResp {
  items: RankingRow[];
  escopo: RelatorioEscopo;
}
export const getRelatorioRanking = async (params?: {
  periodoDias?: number;
  uf?: string;
}): Promise<RelatorioRankingResp> => {
  const { data } = await estudoApi.get<RelatorioRankingResp>(
    "/interno/relatorios/ranking-credenciamento",
    { params }
  );
  return data;
};

export interface EconomiaRow {
  cnpj: string;
  empresa: string | null;
  economiaMesPrometida: number;
  economiaMesRealizada: number;
  delta: number;
  deltaPct: number | null;
  estudoProspectAt: string;
  estudoClienteAt: string;
}
export interface RelatorioEconomiaResp {
  items: EconomiaRow[];
  escopo: RelatorioEscopo;
}
export const getRelatorioEconomia =
  async (): Promise<RelatorioEconomiaResp> => {
    const { data } = await estudoApi.get<RelatorioEconomiaResp>(
      "/interno/relatorios/economia-prometida-realizada"
    );
    return data;
  };

export interface ProdutividadeRow {
  consultor: string;
  nEstudos: number;
  litrosAnalisados: number;
  nConversoes: number;
}
export interface RelatorioProdutividadeResp {
  items: ProdutividadeRow[];
  escopo: RelatorioEscopo;
}
export const getRelatorioProdutividade =
  async (): Promise<RelatorioProdutividadeResp> => {
    const { data } = await estudoApi.get<RelatorioProdutividadeResp>(
      "/interno/relatorios/produtividade"
    );
    return data;
  };

export interface CoberturaRow {
  uf: string | null;
  litrosTotal: number;
  litrosCobertos: number;
  litrosFora: number;
  coberturaPct: number | null;
  nPostos: number;
}
export interface RelatorioCoberturaResp {
  items: CoberturaRow[];
  escopo: RelatorioEscopo;
}
export const getRelatorioCobertura = async (params?: {
  periodoDias?: number;
}): Promise<RelatorioCoberturaResp> => {
  const { data } = await estudoApi.get<RelatorioCoberturaResp>(
    "/interno/relatorios/cobertura-uf",
    { params }
  );
  return data;
};

// ── Inteligência de Preços ──
export interface SemanaPreco {
  semana: string;
  rf: number | null;
  anp: number | null;
  iprf: number | null;
  litros: number;
  n: number;
}
export interface PainelPrecos {
  tipo: "cidade" | "rodovia" | "todos";
  semanas: SemanaPreco[];
  ultima: SemanaPreco | null;
  menor: SemanaPreco | null;
  vsAnp: number | null;
  delta4sem: number | null;
  atualizadoAte: string | null;
  meta: { fonte: string };
}
export const getPainelPrecos = async (params?: {
  tipo?: "cidade" | "rodovia" | "todos";
}): Promise<PainelPrecos> => {
  const { data } = await estudoApi.get<PainelPrecos>("/interno/precos/painel", {
    params,
  });
  return data;
};

export interface UfPreco {
  uf: string;
  rf: number;
  anp: number | null;
  litros: number;
  n: number;
  min: number | null;
  max: number | null;
  vsMedia: number;
  vsAnp: number | null;
}
export interface RankingUfResp {
  semana: string | null;
  mediaNacional: number | null;
  itens: UfPreco[];
  meta: { fonte: string; amostraMinima: number };
}
export const getRankingUf = async (params?: {
  tipo?: "cidade" | "rodovia" | "todos";
}): Promise<RankingUfResp> => {
  const { data } = await estudoApi.get<RankingUfResp>(
    "/interno/precos/ranking-uf",
    { params }
  );
  return data;
};

export interface MovimentoUf {
  uf: string;
  rfUlt: number;
  rfPen: number;
  delta: number;
  deltaPct: number | null;
  n: number;
}
export interface MovimentoResp {
  semana: string | null;
  semanaAnterior: string | null;
  itens: MovimentoUf[];
  meta: { fonte: string; amostraMinima: number };
}
export const getMovimentoUf = async (params?: {
  tipo?: "cidade" | "rodovia" | "todos";
}): Promise<MovimentoResp> => {
  const { data } = await estudoApi.get<MovimentoResp>(
    "/interno/precos/movimento-uf",
    { params }
  );
  return data;
};

export interface DispersaoUf {
  uf: string;
  min: number;
  mediana: number;
  max: number;
  mediaPond: number;
  amplitude: number;
  nMicro: number;
  litros: number;
  n: number;
}
export interface DispersaoResp {
  semana: string | null;
  itens: DispersaoUf[];
  meta: { fonte: string; amostraMinima: number };
}
export const getDispersaoUf = async (params?: {
  tipo?: "cidade" | "rodovia" | "todos";
}): Promise<DispersaoResp> => {
  const { data } = await estudoApi.get<DispersaoResp>(
    "/interno/precos/dispersao-uf",
    { params }
  );
  return data;
};

export interface AlertaMicro {
  microKey: string;
  uf: string;
  rfUlt: number;
  rfPen: number;
  delta: number;
  deltaPct: number | null;
  n: number;
}
export interface AlertasResp {
  semana: string | null;
  semanaAnterior: string | null;
  altas: AlertaMicro[];
  quedas: AlertaMicro[];
  meta: { fonte: string; amostraMinima: number };
}
export const getAlertasPrecos = async (params?: {
  tipo?: "cidade" | "rodovia" | "todos";
  limite?: number;
}): Promise<AlertasResp> => {
  const { data } = await estudoApi.get<AlertasResp>(
    "/interno/precos/alertas",
    { params }
  );
  return data;
};
